import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import LoadingSpinner from '../../components/UI/LoadingSpinner';
import { useAuth } from '../../contexts/AuthContext';

// Resolve the Node API base URL (mirrors the pattern used across the app).
const getApiUrl = () => {
  if (process.env.REACT_APP_API_URL) return process.env.REACT_APP_API_URL;
  const { hostname, protocol } = window.location;
  if (hostname === 'localhost' || hostname === '127.0.0.1') return 'http://localhost:3000';
  return `${protocol}//${hostname}`;
};

const fmt = (n, d = 2) =>
  n === null || n === undefined || isNaN(n) ? '—' : Number(n).toFixed(d);

const pct = (n) => {
  if (n === null || n === undefined || isNaN(n)) return '—';
  const v = Number(n);
  return `${v >= 0 ? '+' : ''}${v.toFixed(2)}%`;
};

// Parse a YYYY-MM-DD string as a LOCAL date (avoids the UTC-midnight off-by-one
// that otherwise shifts e.g. "2026-09-01" to Aug 31 in western timezones).
const parseLocalDate = (d) => {
  if (!d) return null;
  if (typeof d === 'string') {
    const m = d.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (m) return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
  }
  const dt = new Date(d);
  return isNaN(dt) ? null : dt;
};

const fmtDate = (d) => {
  const dt = parseLocalDate(d);
  if (!dt) return d || '—';
  return dt.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

// Format a date, rolling weekends forward to the next business day (markets are
// closed Sat/Sun, so the real entry is the following Monday).
const fmtBusinessDate = (d) => {
  const dt = parseLocalDate(d);
  if (!dt) return d || '—';
  const day = dt.getDay(); // 0=Sun, 6=Sat
  if (day === 6) dt.setDate(dt.getDate() + 2);
  else if (day === 0) dt.setDate(dt.getDate() + 1);
  return dt.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

// Entry date for a monthly sector pick: the signal date is the DECISION month;
// the position is actually entered on day 1 of the NEXT month (rolled to the
// next business day if that lands on a weekend). e.g. 2026-09-01 signal -> Oct 1.
const fmtEntryDate = (d) => {
  const dt = parseLocalDate(d);
  if (!dt) return d || '—';
  dt.setMonth(dt.getMonth() + 1);        // decision month -> action month
  dt.setDate(1);                         // day 1 of the action month
  const day = dt.getDay();
  if (day === 6) dt.setDate(dt.getDate() + 2);
  else if (day === 0) dt.setDate(dt.getDate() + 1);
  return dt.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
};

// Company logo with a gradient-initial fallback (same source/behavior as Dashboard).
const StockLogo = ({ symbol }) => (
  <div className="w-12 h-12 bg-white rounded-lg flex items-center justify-center flex-shrink-0">
    <img
      src={`https://financialmodelingprep.com/image-stock/${symbol}.png`}
      alt={`${symbol} logo`}
      className="w-8 h-8 object-contain"
      onError={(e) => {
        e.target.style.display = 'none';
        if (e.target.nextSibling) e.target.nextSibling.style.display = 'flex';
      }}
    />
    <div
      className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded flex items-center justify-center text-white font-bold text-sm"
      style={{ display: 'none' }}
    >
      {symbol ? symbol.charAt(0) : '?'}
    </div>
  </div>
);

const StatusBadge = ({ status }) => {
  const map = {
    Open: 'bg-green-100 text-green-800',
    Stopped: 'bg-red-100 text-red-800',
    Closed: 'bg-gray-100 text-gray-700',
    BUY: 'bg-green-100 text-green-800',
  };
  return (
    <span className={`inline-flex px-3 py-1 text-sm font-semibold rounded-full ${map[status] || 'bg-blue-100 text-blue-800'}`}>
      {status}
    </span>
  );
};

const statusFromExit = (exitReason, exitDate) => {
  if (exitReason === 'OPEN' || (!exitReason && !exitDate)) return 'Open';
  if (exitReason === 'STOP_HIT') return 'Stopped';
  return 'Closed';
};

const Metric = ({ label, value, valueClass = 'text-gray-900' }) => (
  <div className="bg-gray-50 rounded-lg p-4">
    <p className="text-sm text-gray-600 mb-1">{label}</p>
    <p className={`text-2xl font-bold ${valueClass}`}>{value}</p>
  </div>
);

// A self-contained "Add to watchlist" toggle for a pick card. Shows "☆ Add to
// watchlist" when the symbol isn't tracked and "★ In watchlist" (click to remove)
// when it is. Only rendered for logged-in users (watchlist requires auth).
const WatchlistButton = ({ symbol, inWatchlist, onChange }) => {
  const [busy, setBusy] = useState(false);
  if (!symbol) return null;
  const sym = symbol.toUpperCase();
  const authHdr = () => {
    const t = localStorage.getItem('token');
    return t ? { Authorization: `Bearer ${t}` } : {};
  };
  const add = async () => {
    setBusy(true);
    try {
      const res = await fetch(`${getApiUrl()}/api/watchlist?symbol=${encodeURIComponent(sym)}`, {
        method: 'POST', headers: authHdr(),
      });
      const json = await res.json();
      if (res.ok && json.status === 'ok') { onChange(sym, true); toast.success(`${sym} added to watchlist`); }
      else toast.error(json.error || json.message || 'Could not add');
    } catch (_) { toast.error('Could not add to watchlist'); }
    finally { setBusy(false); }
  };
  const remove = async () => {
    setBusy(true);
    try {
      await fetch(`${getApiUrl()}/api/watchlist?symbol=${encodeURIComponent(sym)}`, {
        method: 'DELETE', headers: authHdr(),
      });
      onChange(sym, false);
      toast.success(`${sym} removed from watchlist`);
    } catch (_) { toast.error('Could not remove from watchlist'); }
    finally { setBusy(false); }
  };
  return inWatchlist ? (
    <button type="button" onClick={remove} disabled={busy}
      className="inline-flex items-center gap-1 text-sm text-green-600 hover:text-red-600 disabled:opacity-50"
      title="In your watchlist — click to remove">
      <span>★</span> In watchlist
    </button>
  ) : (
    <button type="button" onClick={add} disabled={busy}
      className="inline-flex items-center gap-1 text-sm text-blue-600 hover:text-blue-800 disabled:opacity-50"
      title="Add to your watchlist">
      <span>☆</span> Add to watchlist
    </button>
  );
};

// A Dashboard-style card for a single stock pick / signal.
const StockPickCard = ({
  symbol,
  name,
  status,
  dateLabel,
  dateValue,
  businessDate,
  entryDate,
  entryPrice,
  currentOrExitLabel,
  currentOrExitPrice,
  returnPct,
  marketCapB,
  description,
  fundamentals,
  aiRationale,
  aiRiskNotes,
  showWatchlist,
  inWatchlist,
  onWatchlistChange,
}) => {
  const ret = Number(returnPct);
  const retClass = isNaN(ret) ? 'text-gray-900' : ret >= 0 ? 'text-green-600' : 'text-red-600';
  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden hover:shadow-xl transition-shadow duration-300">
      <div className="p-6">
        <div className="flex items-start justify-between mb-4">
          <div className="flex items-center space-x-4">
            <StockLogo symbol={symbol} />
            <div>
              <h3 className="text-xl font-bold text-gray-900">{symbol}</h3>
              {name && <p className="text-gray-600">{name}</p>}
              {showWatchlist && (
                <div className="mt-1">
                  <WatchlistButton symbol={symbol} inWatchlist={inWatchlist} onChange={onWatchlistChange} />
                </div>
              )}
            </div>
          </div>
          <div className="text-right">
            <StatusBadge status={status} />
            <p className="text-sm text-gray-500 mt-1">
              {dateLabel}: {entryDate ? fmtEntryDate(dateValue) : businessDate ? fmtBusinessDate(dateValue) : fmtDate(dateValue)}
            </p>
            {marketCapB != null && !isNaN(marketCapB) && (
              <p className="text-xs text-gray-400 mt-1">Cap: ${fmt(marketCapB, 0)}B</p>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-4">
          <Metric label="Entry Price" value={`$${fmt(entryPrice)}`} />
          <Metric
            label={currentOrExitLabel}
            value={`$${fmt(currentOrExitPrice)}`}
            valueClass="text-blue-600"
          />
          <Metric label="Return" value={pct(returnPct)} valueClass={retClass} />
        </div>

        {fundamentals && (fundamentals.pe != null || fundamentals.eps != null ||
          fundamentals.marketCapB != null || fundamentals.targetPrice != null ||
          fundamentals.epsGrowth != null || fundamentals.revGrowth != null) && (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-2 mb-4 text-sm">
            {fundamentals.pe != null && (
              <div className="flex justify-between border-b border-gray-50 py-1">
                <span className="text-gray-500">P/E</span><span className="font-medium text-gray-900">{fmt(fundamentals.pe, 1)}</span>
              </div>
            )}
            {fundamentals.eps != null && (
              <div className="flex justify-between border-b border-gray-50 py-1">
                <span className="text-gray-500">EPS</span><span className="font-medium text-gray-900">${fmt(fundamentals.eps)}</span>
              </div>
            )}
            {fundamentals.marketCapB != null && (
              <div className="flex justify-between border-b border-gray-50 py-1">
                <span className="text-gray-500">Mkt Cap</span><span className="font-medium text-gray-900">${fmt(fundamentals.marketCapB, 0)}B</span>
              </div>
            )}
            {fundamentals.targetPrice != null && (
              <div className="flex justify-between border-b border-gray-50 py-1">
                <span className="text-gray-500">Analyst Target</span><span className="font-medium text-blue-600">${fmt(fundamentals.targetPrice)}</span>
              </div>
            )}
            {fundamentals.revGrowth != null && (
              <div className="flex justify-between border-b border-gray-50 py-1">
                <span className="text-gray-500">Rev Growth</span>
                <span className={`font-medium ${Number(fundamentals.revGrowth) >= 0 ? 'text-green-600' : 'text-red-600'}`}>{pct(Number(fundamentals.revGrowth) * 100)}</span>
              </div>
            )}
            {fundamentals.epsGrowth != null && (
              <div className="flex justify-between border-b border-gray-50 py-1">
                <span className="text-gray-500">EPS Growth</span>
                <span className={`font-medium ${Number(fundamentals.epsGrowth) >= 0 ? 'text-green-600' : 'text-red-600'}`}>{pct(Number(fundamentals.epsGrowth) * 100)}</span>
              </div>
            )}
          </div>
        )}

        {description && (
          <div className="mb-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">Description</h4>
            <p className="text-gray-700 leading-relaxed text-sm">{description}</p>
          </div>
        )}

        {(aiRationale || aiRiskNotes) && (
          <div className="border-t border-gray-100 pt-4">
            <h4 className="text-sm font-semibold text-gray-700 mb-2">AI Risk Review</h4>
            {aiRationale && (
              <p className="text-gray-700 leading-relaxed text-sm">{aiRationale}</p>
            )}
            {aiRiskNotes && (
              <p className="text-xs text-gray-500 mt-2">
                <span className="font-semibold">Risk:</span> {aiRiskNotes}
              </p>
            )}
            <p className="text-[11px] text-gray-400 mt-2">
              AI-generated, advisory only — not a recommendation.
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

const Section = ({ title, subtitle, children }) => (
  <div className="mb-10">
    <div className="mb-4">
      <h2 className="text-2xl font-bold text-gray-900">{title}</h2>
      {subtitle && <p className="text-gray-600 mt-1">{subtitle}</p>}
    </div>
    {children}
  </div>
);

// Locked-feature prompt shown to non-premium members in place of gated picks.
const UpgradePrompt = ({ feature }) => (
  <div className="bg-gradient-to-br from-blue-50 to-indigo-50 border border-blue-200 rounded-xl p-8 text-center">
    <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-blue-100 flex items-center justify-center">
      <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2"
          d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
      </svg>
    </div>
    <h3 className="text-lg font-semibold text-gray-900 mb-1">{feature} is a Premium feature</h3>
    <p className="text-gray-600 text-sm mb-4 max-w-md mx-auto">
      Upgrade to Premium to unlock {feature}. It's free right now — just activate Premium on your account.
    </p>
    <a href="/pricing" className="inline-flex items-center px-5 py-2 bg-blue-600 text-white text-sm font-semibold rounded-md hover:bg-blue-700">
      Unlock Premium
    </a>
  </div>
);

const Strategy = () => {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // Active tab: 'top' | 'breakout' | 'value'. Replaces the long single-scroll page.
  const [activeTab, setActiveTab] = useState('top');

  // Logged-in user's watchlist symbols, so pick cards can show Add / In-watchlist.
  const [watchlist, setWatchlist] = useState(() => new Set());
  const onWatchlistChange = (sym, isIn) =>
    setWatchlist((prev) => {
      const n = new Set(prev);
      if (isIn) n.add(sym.toUpperCase()); else n.delete(sym.toUpperCase());
      return n;
    });

  useEffect(() => {
    if (!user) { setWatchlist(new Set()); return; }
    let cancelled = false;
    (async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${getApiUrl()}/api/watchlist?cache=${Date.now()}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) return;
        const json = await res.json();
        if (!cancelled && Array.isArray(json)) {
          setWatchlist(new Set(json.map((w) => (w.symbol || '').toUpperCase())));
        }
      } catch (_) { /* non-fatal */ }
    })();
    return () => { cancelled = true; };
  }, [user]);

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${getApiUrl()}/api/strategy/dashboard?cache=${Date.now()}`, {
          headers: token ? { Authorization: `Bearer ${token}` } : {},
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        if (json.error) throw new Error(json.error);
        setData(json);
      } catch (e) {
        setError(e.message);
      } finally {
        setLoading(false);
      }
    };
    fetchDashboard();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center max-w-md">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Strategy data unavailable</h2>
          <p className="text-gray-600">{error || 'No data returned.'}</p>
        </div>
      </div>
    );
  }

  const { sectorStockPick = {}, individualSignals = [], premium = false, gatingEnabled = false } = data;
  // Gating is active only when the server flag is on AND the user is not premium.
  // While Premium payment is in test mode the flag is off, so nobody is gated.
  const gated = gatingEnabled && !premium;

  // Sort open positions best-first (by current return %, descending). Rows with
  // no return sink to the bottom.
  const byReturnDesc = (a, b) => {
    const av = Number(a.perf_current); const bv = Number(b.perf_current);
    const an = Number.isFinite(av); const bn = Number.isFinite(bv);
    if (an && bn) return bv - av;
    if (an) return -1;
    if (bn) return 1;
    return 0;
  };

  const breakouts = individualSignals
    .filter((s) => s.signaltype === 'CONSOLIDATION_BREAKOUT_OVERSOLD')
    .slice()
    .sort(byReturnDesc);

  // Split breakouts like the Value tab: OPEN positions up top (sorted best-first),
  // CLOSED/STOPPED collapsed into a "Recent trade history" section.
  const breakoutOpen = breakouts.filter(
    (s) => statusFromExit(s.exit_reason, s.exit_date) === 'Open'
  );
  const breakoutClosed = breakouts.filter(
    (s) => statusFromExit(s.exit_reason, s.exit_date) !== 'Open'
  );

  // Value-oriented signal group. Only rows with an entry price (future-period
  // entries have none yet).
  const priceDownCci = individualSignals.filter(
    (s) =>
      s.signaltype === 'PRICE_DOWN_50_PERCENT_DIVIDEND_CCI_BELOW_MINUS_100' &&
      s.entry_price != null
  );
  // Split value picks: keep OPEN positions up top, collapse CLOSED/STOPPED ones
  // into a "Recent trade history" section (matches the Top Pick layout).
  // Open positions are sorted by return descending (best performers first).
  const valueOpen = priceDownCci
    .filter((s) => statusFromExit(s.exit_reason, s.exit_date) === 'Open')
    .slice()
    .sort(byReturnDesc);
  const valueClosed = priceDownCci.filter(
    (s) => statusFromExit(s.exit_reason, s.exit_date) !== 'Open'
  );

  const topStocks = sectorStockPick.topStocks || [];
  // Recent trade history shows CLOSED trades only (open position is shown above as the current pick).
  const closedTopStocks = topStocks.filter((s) => s.exit_date);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {user && (
          <div className="mb-6 flex flex-wrap items-center justify-between gap-2 bg-white rounded-lg shadow-sm px-5 py-4">
            <div>
              <h2 className="text-xl font-bold text-gray-900">
                Welcome back, {user.firstName || user.name || 'Member'}!
              </h2>
              <p className="text-sm text-gray-500 mt-0.5">
                Current Plan:{' '}
                <span className={`font-semibold ${premium ? 'text-blue-600' : 'text-gray-700'}`}>
                  {premium ? 'Premium' : ((user.subscription || 'Free').charAt(0).toUpperCase() + (user.subscription || 'Free').slice(1))}
                </span>
              </p>
            </div>
            {!premium && (
              <a href="/pricing" className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-semibold rounded-md hover:bg-blue-700">
                Upgrade to Premium
              </a>
            )}
          </div>
        )}

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">AI Stock Picks</h1>
          <p className="text-gray-600 mt-2">
            High-conviction ideas identified by the GandalStock engine, with live performance.
          </p>
        </div>

        {/* Tabs — replaces the long single-scroll layout */}
        <div className="mb-8 border-b border-gray-200">
          <nav className="-mb-px flex flex-wrap gap-x-6" aria-label="Stock pick categories">
            {[
              { key: 'top', label: 'Top Pick' },
              { key: 'breakout', label: 'Breakout Picks' },
              { key: 'value', label: 'Value Picks' },
            ].map((t) => (
              <button
                key={t.key}
                type="button"
                onClick={() => setActiveTab(t.key)}
                className={`whitespace-nowrap border-b-2 py-3 px-1 text-sm font-medium transition-colors ${
                  activeTab === t.key
                    ? 'border-blue-600 text-blue-600'
                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                }`}
                aria-current={activeTab === t.key ? 'page' : undefined}
              >
                {t.label}
              </button>
            ))}
          </nav>
        </div>

        {/* ---------------- Top Pick ---------------- */}
        {activeTab === 'top' && (
        <Section
          title="Top Pick"
          subtitle="Our highest-conviction single-stock idea."
        >
          {sectorStockPick.active === false ? (
            <div className="text-center py-10 bg-gray-50 rounded-lg">
              <p className="text-gray-500">
                {sectorStockPick.reason ||
                  sectorStockPick.currentPickStatus ||
                  'No active pick right now.'}
              </p>
            </div>
          ) : (
            <>
              {sectorStockPick.currentPick ? (
                <div className="mb-6">
                  <StockPickCard
                    symbol={sectorStockPick.currentPick}
                    showWatchlist={!!user}
                    inWatchlist={watchlist.has((sectorStockPick.currentPick || '').toUpperCase())}
                    onWatchlistChange={onWatchlistChange}
                    name={
                      sectorStockPick.currentPickName ||
                      (sectorStockPick.winningEtf
                        ? `Sector leader — ${sectorStockPick.winningEtf}`
                        : 'Current sector pick')
                    }
                    status="Open"
                    dateLabel="Since"
                    dateValue={sectorStockPick.currentPickDate}
                    entryDate
                    entryPrice={topStocks[0] && topStocks[0].symbol === sectorStockPick.currentPick ? topStocks[0].entry_price : null}
                    currentOrExitLabel="Current Price"
                    currentOrExitPrice={topStocks[0] && topStocks[0].symbol === sectorStockPick.currentPick ? topStocks[0].exit_price : null}
                    returnPct={topStocks[0] && topStocks[0].symbol === sectorStockPick.currentPick ? topStocks[0].return_pct : null}
                    description={sectorStockPick.currentPickDescription}
                    fundamentals={{
                      pe: sectorStockPick.currentPickPe,
                      eps: sectorStockPick.currentPickEps,
                      marketCapB: sectorStockPick.currentPickMarketCapB,
                      targetPrice: sectorStockPick.currentPickTargetPrice,
                      epsGrowth: sectorStockPick.currentPickEpsGrowth,
                      revGrowth: sectorStockPick.currentPickRevGrowth,
                    }}
                    aiRationale={sectorStockPick.aiRationale}
                    aiRiskNotes={sectorStockPick.aiRiskNotes}
                  />
                </div>
              ) : (
                <div className="text-center py-10 bg-gray-50 rounded-lg mb-6">
                  <p className="text-gray-500">
                    {sectorStockPick.currentPickStatus || 'Flat — no open position.'}
                  </p>
                </div>
              )}

              {/* Recent trade history — gated like Breakout/Value (flag-controlled).
                  When gated (flag on + non-premium) it is hidden behind an upgrade
                  prompt; while the flag is OFF (test mode) everyone sees it. */}
              {closedTopStocks.length > 0 && !gated && (
                <details className="group mt-2">
                  <summary className="flex items-center justify-between cursor-pointer list-none select-none py-2">
                    <h3 className="text-lg font-semibold text-gray-900">
                      Recent trade history
                      <span className="ml-2 text-sm font-normal text-gray-500">
                        ({Math.min(closedTopStocks.length, 10)})
                      </span>
                    </h3>
                    <svg
                      className="w-5 h-5 text-gray-500 transition-transform duration-200 group-open:rotate-180"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      aria-hidden="true"
                    >
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                    </svg>
                  </summary>
                  <div className="grid gap-6 grid-cols-1 mt-4">
                    {closedTopStocks.slice(0, 10).map((s, i) => (
                      <StockPickCard
                        key={`${s.symbol}-${i}`}
                        symbol={s.symbol}
                        showWatchlist={!!user}
                        inWatchlist={watchlist.has((s.symbol || '').toUpperCase())}
                        onWatchlistChange={onWatchlistChange}
                        name={s.name}
                        status="Closed"
                        dateLabel="Entry"
                        dateValue={s.entry_date}
                        entryPrice={s.entry_price}
                        currentOrExitLabel="Exit Price"
                        currentOrExitPrice={s.exit_price}
                        returnPct={s.return_pct}
                        description={s.description}
                        aiVerdict={s.ai_verdict}
                        aiConfidence={s.ai_confidence}
                        aiRationale={s.ai_rationale}
                        aiRiskNotes={s.ai_risk_notes}
                      />
                    ))}
                  </div>
                </details>
              )}
              {/* When gated, the Top Pick track record is Premium-only. Shown
                  whenever gated (not dependent on closed history, which the backend
                  strips when gated) so it stays consistent with Breakout/Value. */}
              {gated && (
                <div className="mt-6">
                  <UpgradePrompt feature="the full Top Pick track record" />
                </div>
              )}
            </>
          )}
        </Section>
        )}

        {/* ---------------- Breakout Picks (Premium; 1-pick teaser for Free) ---------------- */}
        {activeTab === 'breakout' && (breakouts.length > 0 || gated) && (
          <Section
            title="Breakout Picks"
            subtitle={!gated ? 'Large-cap breakout opportunities.' : 'A preview — unlock all with Premium.'}
          >
            {(() => {
              const renderBreakoutCard = (s, i) => (
                <StockPickCard
                  key={`${s.symbol}-${s.signaldate || i}`}
                  symbol={s.symbol}
                  showWatchlist={!!user}
                  inWatchlist={watchlist.has((s.symbol || '').toUpperCase())}
                  onWatchlistChange={onWatchlistChange}
                  name={s.name}
                  status={statusFromExit(s.exit_reason, s.exit_date)}
                  dateLabel="Entry"
                  dateValue={s.entry_date}
                  entryPrice={s.entry_price}
                  currentOrExitLabel={
                    statusFromExit(s.exit_reason, s.exit_date) === 'Open' ? 'Current Price' : 'Exit Price'
                  }
                  currentOrExitPrice={
                    statusFromExit(s.exit_reason, s.exit_date) === 'Open'
                      ? s.current_price
                      : (s.exit_price != null ? s.exit_price : s.current_price)
                  }
                  returnPct={s.perf_current}
                  marketCapB={s.marketcap_b}
                  description={s.stock_description}
                  fundamentals={{
                    pe: s.pe, eps: s.eps,
                    targetPrice: s.target_price, epsGrowth: s.eps_growth, revGrowth: s.rev_growth,
                  }}
                  aiRationale={s.ai_rationale}
                  aiRiskNotes={s.ai_risk_notes}
                />
              );
              if (gated) {
                return (
                  <>
                    <div className="grid gap-6 grid-cols-1">
                      {breakoutOpen.map(renderBreakoutCard)}
                    </div>
                    <div className="mt-6">
                      <UpgradePrompt feature="all Breakout Picks" />
                    </div>
                  </>
                );
              }
              return (
                <>
                  {breakoutOpen.length > 0 ? (
                    <div className="grid gap-6 grid-cols-1">
                      {breakoutOpen.map(renderBreakoutCard)}
                    </div>
                  ) : (
                    <div className="text-center py-8 bg-gray-50 rounded-lg">
                      <p className="text-gray-500">No open breakout positions right now.</p>
                    </div>
                  )}

                  {breakoutClosed.length > 0 && (
                    <details className="group mt-6">
                      <summary className="flex items-center justify-between cursor-pointer list-none select-none py-2">
                        <h3 className="text-lg font-semibold text-gray-900">
                          Recent trade history
                          <span className="ml-2 text-sm font-normal text-gray-500">
                            ({breakoutClosed.length})
                          </span>
                        </h3>
                        <svg
                          className="w-5 h-5 text-gray-500 transition-transform duration-200 group-open:rotate-180"
                          fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        </svg>
                      </summary>
                      <div className="grid gap-6 grid-cols-1 mt-4">
                        {breakoutClosed.map(renderBreakoutCard)}
                      </div>
                    </details>
                  )}
                </>
              );
            })()}
          </Section>
        )}
        {activeTab === 'breakout' && breakouts.length === 0 && !gated && (
          <Section title="Breakout Picks" subtitle="Large-cap breakout opportunities.">
            <div className="text-center py-10 bg-gray-50 rounded-lg">
              <p className="text-gray-500">No breakout picks right now. Check back soon.</p>
            </div>
          </Section>
        )}

        {/* ---------------- Value Picks ---------------- */}
        {activeTab === 'value' && (priceDownCci.length > 0 || gated) && (
          <Section
            title="Value Picks"
            subtitle={!gated ? 'Undervalued opportunities.' : 'A preview — unlock all with Premium.'}
          >
            {(() => {
              const renderValueCard = (s, i) => (
                <StockPickCard
                  key={`${s.symbol}-${s.signaldate || i}`}
                  symbol={s.symbol}
                  showWatchlist={!!user}
                  inWatchlist={watchlist.has((s.symbol || '').toUpperCase())}
                  onWatchlistChange={onWatchlistChange}
                  name={s.name}
                  status={statusFromExit(s.exit_reason, s.exit_date)}
                  dateLabel="Entry"
                  dateValue={s.entry_date}
                  entryPrice={s.entry_price}
                  currentOrExitLabel={
                    statusFromExit(s.exit_reason, s.exit_date) === 'Open'
                      ? 'Current Price'
                      : 'Exit Price'
                  }
                  currentOrExitPrice={
                    statusFromExit(s.exit_reason, s.exit_date) === 'Open'
                      ? s.current_price
                      : s.exit_price != null
                      ? s.exit_price
                      : s.current_price
                  }
                  returnPct={s.perf_current}
                  marketCapB={s.marketcap_b}
                  description={s.stock_description}
                  fundamentals={{
                    pe: s.pe, eps: s.eps,
                    targetPrice: s.target_price, epsGrowth: s.eps_growth, revGrowth: s.rev_growth,
                  }}
                  aiRationale={s.ai_rationale}
                  aiRiskNotes={s.ai_risk_notes}
                />
              );
              if (gated) {
                return (
                  <>
                    <div className="grid gap-6 grid-cols-1">
                      {valueOpen.map(renderValueCard)}
                    </div>
                    <div className="mt-6">
                      <UpgradePrompt feature="all Value Picks" />
                    </div>
                  </>
                );
              }
              return (
                <>
                  {valueOpen.length > 0 ? (
                    <div className="grid gap-6 grid-cols-1">
                      {valueOpen.map(renderValueCard)}
                    </div>
                  ) : (
                    <div className="text-center py-8 bg-gray-50 rounded-lg">
                      <p className="text-gray-500">No open value positions right now.</p>
                    </div>
                  )}

                  {valueClosed.length > 0 && (
                    <details className="group mt-6">
                      <summary className="flex items-center justify-between cursor-pointer list-none select-none py-2">
                        <h3 className="text-lg font-semibold text-gray-900">
                          Recent trade history
                          <span className="ml-2 text-sm font-normal text-gray-500">
                            ({valueClosed.length})
                          </span>
                        </h3>
                        <svg
                          className="w-5 h-5 text-gray-500 transition-transform duration-200 group-open:rotate-180"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                          aria-hidden="true"
                        >
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7" />
                        </svg>
                      </summary>
                      <div className="grid gap-6 grid-cols-1 mt-4">
                        {valueClosed.map(renderValueCard)}
                      </div>
                    </details>
                  )}
                </>
              );
            })()}
          </Section>
        )}
        {activeTab === 'value' && priceDownCci.length === 0 && !gated && (
          <Section title="Value Picks" subtitle="Undervalued opportunities.">
            <div className="text-center py-10 bg-gray-50 rounded-lg">
              <p className="text-gray-500">No value picks right now. Check back soon.</p>
            </div>
          </Section>
        )}

        <p className="text-xs text-gray-400 mt-6">
          Informational only — not investment advice. Backtested strategies; past
          performance does not guarantee future results.
        </p>
      </div>
    </div>
  );
};

export default Strategy;
